<template>
    <div class="service-subscriptions-import" :class="{ loading: loading }">
        <div v-if="project_id && file">
            <form @submit.prevent="openExecuteModal" method="POST" accept-charset="UTF-8" data-vv-scope="execute"
                novalidate="novalidate">

                <div class="row" v-if="project">
                    <div class="col-xs-4 form-group">
                        <dl class="no-margin">
                            <dt>
                                Property
                            </dt>
                            <dd>
                                {{ project.name }}
                            </dd>
                        </dl>
                    </div>
                    <div class="col-xs-4 form-group">
                        <dl class="no-margin">
                            <dt>
                                Mode
                            </dt>
                            <dd>
                                {{ mode | titleCase }}
                            </dd>
                        </dl>
                    </div>
                    <div class="col-xs-4 text-right form-group">
                        <label>&nbsp;</label>

                        <button v-if="canExecute" type="button" class="btn btn-warning" @click="openExecuteModal"
                            :disabled="loading">
                            Execute
                        </button>


                        <dropdown menu-right>
                            <btn class="dropdown-toggle" type="primary">
                                <i class="fa fa-cog" aria-hidden="true"></i>
                            </btn>

                            <template slot="dropdown">
                                <li>
                                    <a role="button" @click="openModal" :disabled="loading">
                                        Load Import File
                                    </a>
                                </li>
                            </template>
                        </dropdown>
                    </div>
                </div>

                <table class="table table-responsive">
                    <colgroup>
                        <col v-for="(heading, index) in headings"
                            :class="[typeof heading.value === 'string' ? getColumnClass(index) : '']">
                    </colgroup>
                    <thead>
                        <tr>
                            <th v-for="(heading, index) in headings">
                                <select class="form-control margin-md-bottom" :data-index="index" v-model="heading.value"
                                    @change="onHeadingChange">
                                    <option value="">-</option>
                                    <option v-for="heading in headingOptions" :value="heading">
                                        {{ heading | titleCase }}
                                    </option>
                                </select>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in data">
                            <td v-for="cellValue in row">
                                {{ cellValue }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </form>
        </div>

        <div v-else class="form-group padding-md-top padding-md-bottom text-center">
            <button class="btn btn-warning" type="button" @click="openModal" :disabled="loading">
                Set Import Options
            </button>
        </div>

        <modal :name=modalId :adaptive="true" :scrollable="true" :clickToClose="false" height="auto">
            <div class="dashboard-block" :class="{ loading: loading }">
                <form @submit.prevent="setOptions" method="POST" accept-charset="UTF-8" class="margin-md-top"
                    data-vv-scope="workbook" novalidate="novalidate">
                    <fieldset>
                        <legend>Import Options</legend>
                    </fieldset>

                    <div class="btn-group btn-group-justified form-group" role="group">
                        <div class="btn-group" role="group">
                            <button type="button" :class="{ active: mode === 'replace' }" class="btn btn-sm btn-success"
                                @click="mode = 'replace'">
                                Replace All
                            </button>
                        </div>
                        <div class="btn-group" role="group">
                            <button type="button" :class="{ active: mode === 'add' }" class="btn btn-sm btn-primary"
                                @click="mode = 'add'">
                                Only Add
                            </button>
                        </div>
                    </div>

                    <div class="important form-group">
                        <p class="no-margin" v-if="mode === 'replace'">
                            All Service Availability records for this property will be added/updated (matched by unit).
                            All others will be archived.
                        </p>
                        <p class="no-margin" v-else>
                            Service Availability will be added/updated without archiving unmatched records.
                        </p>
                    </div>

                    <div class="form-group has-feedback" :class="{ 'has-error': errors.has('workbook.project_id') }">
                        <label for="project-id">Project</label>

                        <select id="project-id" v-model="project_id" name="project_id" class="form-control"
                            v-validate="'required'" aria-required="true" required="true">
                            <option value="">-</option>
                            <option v-for="project in projects" :value="project.id">
                                {{ project.name }}
                            </option>
                        </select>

                        <span v-show="errors.has('workbook.project_id')" class="help-block">
                            {{ errors.first('workbook.project_id') }}
                        </span>
                    </div>

                    <div class="form-group has-feedback"
                        :class="{ 'has-error': errors.has('workbook.ServiceSubscription[file]') }">
                        <label for="service-subscription-file">Import File</label>

                        <input v-validate="'required'" aria-required="true" required="true" id="service-subscription-file"
                            name="ServiceSubscription[file]" type="file" class="form-control dropable"
                            @change="onFileChange">

                        <span v-show="errors.has('workbook.ServiceSubscription[file]')" class="help-block">
                            {{ errors.first('workbook.ServiceSubscription[file]') }}
                        </span>
                    </div>

                    <hr>

                    <div class="row">
                        <div class="col-sm-6 col-ld-4 form-group">
                            <a class="btn btn-link" :disabled="loading" @click="closeModal">
                                Close
                            </a>
                        </div>

                        <div class="col-sm-6 col-ld-4 form-group pull-right">
                            <button type="submit" class="btn btn-success btn-block" :disabled="loading">
                                Load Data
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </modal>

        <modal name="confirm-execute" :adaptive="true" :scrollable="true" height="auto">
            <div class="dashboard-block" :class="{ loading: loading }">
                <fieldset>
                    <legend>Confirm Import</legend>

                    <h4 v-if="importData">
                        <span>Creating/Updating {{ importData.length }} Service Subscriptions</span>
                    </h4>

                    <h6 v-if="project_id && project.name" class="text-bold">{{ project.name }}</h6>

                    <hr>

                    <div class="row">
                        <div class="col-xs-6">
                            <button type="button" class="btn btn-default" @click="$modal.hide('confirm-execute')">
                                Cancel
                            </button>
                        </div>
                        <div class="col-xs-6 text-right">
                            <button role="button" @click="executeImport" :disabled="loading"
                                class="btn btn-success btn-block">
                                Execute Import
                            </button>
                        </div>
                    </div>
                </fieldset>
            </div>
        </modal>
    </div>
</template>

<script>
var XLSX = require('xlsx');

export default {
    props: {
        projectOptions: {
            type: String,
            required: true
        },

        passProjectId: {
            default() {
                return null;
            },
            type: String
        },

        modalId: {
            default() {
                return Math.random().toString();
            },
            type: String
        },
    },

    data() {
        return {
            loading: false,
            file: null,
            data: [],
            headings: [],
            mode: 'replace',

            project_id: null,
            projects: [],

            headingOptions: [
                'subscribed',
                'unit',
                'subscription_price',
            ],

            requiredHeadings: [
                'subscribed',
                'unit',
            ]
        };
    },

    created() {
        this.init();
    },

    computed: {
        canExecute() {
            return _.isEmpty(_.difference(this.requiredHeadings, _.map(this.headings, 'value')));
        },

        importData() {
            return _.reduce(this.data, (carry, rowData, rowIndex) => {
                var row = {};

                _.each(this.headings, (heading, headingIndex) => {
                    if (!_.isEmpty(heading.value)) {
                        row[`ServiceSubscription[${rowIndex}][${heading.value}]`] = rowData[headingIndex];
                    }
                });

                carry.push(row);

                return carry;
            }, []);
        },

        importForm() {
            var form = document.createElement('form');
            form.setAttribute('method', 'POST');
            form.setAttribute('action', '/serviceSubscriptions/import');

            var hiddenField = document.createElement('input');
            hiddenField.setAttribute('type', 'hidden');
            hiddenField.setAttribute('name', '_method');
            hiddenField.setAttribute('value', 'POST');

            var token = $('meta[name="csrf-token"]').attr('content');
            var tokenField = document.createElement('input');
            tokenField.setAttribute('type', 'hidden');
            tokenField.setAttribute('name', '_token');
            tokenField.setAttribute('value', token);

            form.appendChild(tokenField);
            form.appendChild(hiddenField);

            if (this.mode) {
                var modeField = document.createElement('input');
                modeField.setAttribute('type', 'hidden');
                modeField.setAttribute('name', 'mode');
                modeField.setAttribute('value', this.mode);

                form.appendChild(modeField);
            }

            if (this.project_id) {
                var projectIdField = document.createElement('input');
                projectIdField.setAttribute('type', 'hidden');
                projectIdField.setAttribute('name', 'project_id');
                projectIdField.setAttribute('value', this.project_id);

                form.appendChild(projectIdField);
            }

            _.each(this.importData, (importRow) => {
                _.each(importRow, (value, key) => {
                    var field = document.createElement('input');
                    field.setAttribute('type', 'hidden');
                    field.setAttribute('name', key);
                    field.setAttribute('value', value);

                    form.appendChild(field);
                });
            });

            return form;
        },

        project() {
            if (this.project_id && this.projects.length) {
                return _.find(this.projects, { id: this.project_id });
            }
        },
    },
    watch: {},
    methods: {
        init() {
            try {
                console.log('passProjectId: ', this.passProjectId)
                console.log('projectOptions: ', this.projectOptions);
                this.$set(this, 'projects', Object.entries(JSON.parse(this.projectOptions)).slice(1).map(([key, value]) => ({ id: key, name: value })));
                console.log('set projects: ', this.projects);
                //this.$set(this, 'project_id', this.passProjectId ?? null); this causes the error
                if (this.passProjectId) {
                    console.log('trying to set project_id: ', this.passProjectId)
                    this.$set(this, 'project_id', this.passProjectId);
                }
            } catch (e) {
                console.error('Error parsing:', e);
            }
        },

        // loadProjects() {
        //     var url = '/api/projects';

        //     return axios.get(url)
        //         .then((response) => {
        //             var projects = _.sortBy(response.data.Projects, 'name');

        //             this.$set(this, 'projects', projects);
        //         });
        // },

        loadWorkbook() {
            var reader = new FileReader();

            reader.onload = (e) => {
                var data = e.target.result;

                this.parseWorkbook(XLSX.read(data, { type: 'array' }));
            };

            reader.readAsArrayBuffer(this.file);
        },

        parseWorkbook(workbook) {
            var sheetName = _.head(workbook.SheetNames);
            var sheet = workbook.Sheets[sheetName];
            var data = XLSX.utils.sheet_to_json(sheet, {
                header: 1,
                defval: null
            });

            this.headings = _.map(_.first(data), (heading) => {
                return {
                    value: null
                };
            });
            this.data = _.slice(data, 1);
        },

        onFileChange(e) {
            this.file = e.target.files[0];
        },

        setOptions() {
            return this.$validator.validateAll('workbook')
                .then((valid) => {
                    if (valid) {
                        this.loadWorkbook();
                        this.closeModal();
                    }
                });
        },

        onHeadingChange(e) {
            var value = e.target.value;

            if (_.isEmpty(value)) {
                value = null;
            }

            if (typeof value === 'string') {
                this.cleanupHeadings(value);
            }

            this.$set(this.headings, $(e.target).attr('data-index'), { value: value });
        },

        cleanupHeadings(value) {
            for (var i = 0; i < this.headings.length; i++) {
                if (this.headings[i].value === value) {
                    this.$set(this.headings, i, { value: null });
                }
            }
        },

        getColumnClass(index) {
            var classNames = [
                'info',
                'success',
                'warning',
                'danger',
                'excellent',
            ];

            return classNames[index % classNames.length];
        },

        openExecuteModal() {
            return this.$validator.validateAll('execute')
                .then((valid) => {
                    if (valid) {
                        this.$modal.show('confirm-execute');
                    }
                });
        },

        executeImport() {
            return this.$validator.validateAll('execute')
                .then((valid) => {
                    if (valid) {
                        this.$modal.hide('confirm-execute');

                        this.loading = true;

                        const form = this.importForm;

                        document.body.appendChild(form);
                        form.submit();
                    }
                });
        },

        closeModal() {
            this.$modal.hide(this.modalId);
        },

        openModal() {
            this.$modal.show(this.modalId);
        },
    }
}
</script>
